import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { I18n } from 'react-redux-i18n'
import { Bar, XAxis } from 'recharts'
import useGetVsic from '../../../../../../common/category/useGetVsic'
import { ChartContainer } from '../../../../../../common/chart/chartContainer'
import { Legend } from '../../../../../../common/chart/legend'
import { Element } from '../../../../../../common/html/element'
import { SCREEN } from '../../../../../../common/selectCurrencyUnit/constant'
import { useFormatCurrency } from '../../../../../../common/selectCurrencyUnit/useFormatCurrency'
import { formatVal } from '../../../../../../helpers/value'
import {
  selectCompareIndustry,
  selectDefaultIndustry,
} from '../../../store/slice'
import { CustomXAxisTick } from '../../common/CustomXAxisTick'
import { KEY_DATA, MAP_TYPE_TITLE } from '../../constant'
import { selectCompareVal, selectData } from '../../store/slice'

const ChartSize = ({ isDynamicReport, settingDynamic = {}, year }) => {
  const data = useSelector(selectData)
  const vsicDefaultStore = useSelector(selectDefaultIndustry)
  const vsicCompareStore = useSelector(selectCompareIndustry)
  const compareValStore = useSelector(selectCompareVal)
  const { vsics } = useGetVsic()
  const locale = useSelector((state) => state.i18n.locale)
  const { formatedUnit, multiplier, formatValueWithUnit, getExchangeRate } =
    useFormatCurrency(SCREEN.INDUSTRY_ANALYSIS)
  const {
    DEFAULT_INDUSTRY: vsicDefaultDyn,
    COMPARE_INDUSTRY: vsicCompareDyn,
    compareBetweenRatio: compareValDyn,
  } = settingDynamic
  const vsicDefault = isDynamicReport ? vsicDefaultDyn : vsicDefaultStore
  const vsicCompare = isDynamicReport ? vsicCompareDyn : vsicCompareStore
  const compareVal = isDynamicReport ? compareValDyn : compareValStore

  const defaultData = data[compareVal]?.defaultIndustry?.size
  const comparingData = data[compareVal]?.comparingIndustry?.size
  const titleDefaultIndustry = vsics.find(
    (item) => item.id === vsicDefault,
  )?.name
  const titleComparingIndustry =
    vsicDefault === vsicCompare
      ? titleDefaultIndustry + '(1)'
      : vsics.find((item) => item.id === vsicCompare)?.name

  const groups = [titleDefaultIndustry, titleComparingIndustry]
  const chartData = useMemo(() => {
    const sizeChecked = settingDynamic.sizeChecked || []
    const result = []

    if (sizeChecked.includes('isTotalAssets') || !isDynamicReport) {
      result.push({
        group: titleDefaultIndustry,
        title: I18n.t('companyAnalysis.industryAnalysis.TOTAL_ASSETS'),
        key1: defaultData?.[0]?.totalAssets,
        key2: defaultData?.[1]?.totalAssets,
      })
    }

    if (sizeChecked.includes('isTotalRevenue') || !isDynamicReport) {
      result.push({
        group: titleDefaultIndustry,
        title: I18n.t('companyAnalysis.industryAnalysis.TOTAL_REVENUE'),
        key1: defaultData?.[0]?.totalRevenue,
        key2: defaultData?.[1]?.totalRevenue,
      })
    }

    if (sizeChecked.includes('isTotalDebt') || !isDynamicReport) {
      result.push({
        group: titleDefaultIndustry,
        title: I18n.t('companyAnalysis.industryAnalysis.TOTAL_DEBT'),
        key1: defaultData?.[0]?.totalDebt,
        key2: defaultData?.[1]?.totalDebt,
      })
    }

    if (vsicCompare) {
      if (sizeChecked.includes('isTotalAssets') || !isDynamicReport) {
        result.push({
          group: titleComparingIndustry,
          title: I18n.t('companyAnalysis.industryAnalysis.TOTAL_ASSETS'),
          key1: comparingData?.[0]?.totalAssets,
          key2: comparingData?.[1]?.totalAssets,
        })
      }

      if (sizeChecked.includes('isTotalRevenue') || !isDynamicReport) {
        result.push({
          group: titleComparingIndustry,
          title: I18n.t('companyAnalysis.industryAnalysis.TOTAL_REVENUE'),
          key1: comparingData?.[0]?.totalRevenue,
          key2: comparingData?.[1]?.totalRevenue,
        })
      }

      if (sizeChecked.includes('isTotalDebt') || !isDynamicReport) {
        result.push({
          group: titleComparingIndustry,
          title: I18n.t('companyAnalysis.industryAnalysis.TOTAL_DEBT'),
          key1: comparingData?.[0]?.totalDebt,
          key2: comparingData?.[1]?.totalDebt,
        })
      }
    }

    return result
  }, [
    defaultData,
    comparingData,
    vsicCompare,
    isDynamicReport,
    settingDynamic,
    titleDefaultIndustry,
    titleComparingIndustry,
  ])

  const exchangeRate = getExchangeRate({ year })
  const dataConvert = chartData.map((item) => ({
    ...item,
    key1: (item.key1 / exchangeRate) * multiplier,
    key2: (item.key2 / exchangeRate) * multiplier,
  }))

  const legendSector = [
    {
      title: MAP_TYPE_TITLE[compareVal][KEY_DATA.VALUE_1],
      dataKey: KEY_DATA.VALUE_1,
      color: '#006AA6',
    },
    {
      title: MAP_TYPE_TITLE[compareVal][KEY_DATA.VALUE_2],
      dataKey: KEY_DATA.VALUE_2,
      color: '#FFCD60',
    },
  ]

  return (
    <>
      <Element style={{ color: '#9C9C9C' }}>
        {chartData.length ? formatedUnit : <>&nbsp;</>}
      </Element>
      <ChartContainer
        data={dataConvert}
        width="100%"
        height={isDynamicReport ? 300 : 350}
        margin={{
          top: isDynamicReport ? 10 : 20,
          right: 30,
          left: 0,
          bottom: 60,
        }}
        yAxis={[
          {
            yAxisId: 'left',
            dataKeys: ['key1', 'key2'],
            isStackedBar: true,
            tickFormatter: (value) => formatVal(value, locale, false, 0),
          },
        ]}
        tooltip={{
          schema: {
            content: [
              {
                dataKey: 'key1',
                label: I18n.t(MAP_TYPE_TITLE[compareVal][KEY_DATA.VALUE_1]),
                formatValue: (value) => formatValueWithUnit({ value }),
              },
              {
                dataKey: 'key2',
                label: I18n.t(MAP_TYPE_TITLE[compareVal][KEY_DATA.VALUE_2]),
                formatValue: (value) => formatValueWithUnit({ value }),
              },
            ],
          },
        }}
        xAxis={[
          {
            dataKey: 'title',
            interval: 0,
            height: 40,
          },
        ]}
      >
        <XAxis
          xAxisId="group"
          dataKey="group"
          axisLine={false}
          tickLine={false}
          height={30}
          interval={0}
          ticks={groups}
          tick={
            <CustomXAxisTick
              titleDefaultIndustry={titleDefaultIndustry}
              isCompare={!!vsicCompare}
              isDynamicReport={isDynamicReport}
            />
          }
        />
        <Bar
          yAxisId="left"
          dataKey="key1"
          stackId="a"
          fill="#006AA6"
          maxBarSize={35}
          isAnimationActive={false}
        />
        <Bar
          yAxisId="left"
          dataKey="key2"
          stackId="a"
          fill="#FFCD60"
          maxBarSize={35}
          isAnimationActive={false}
        />
      </ChartContainer>
      {isDynamicReport ? (
        <Legend
          items={legendSector.map((item) => ({
            ...item,
            isCheckbox: false,
          }))}
          className="flex gap4 justifyContentCenter"
        />
      ) : (
        <></>
      )}
    </>
  )
}

export default ChartSize
